'use client';

import { Company } from "@/app/types/Company";
import { useEffect, useState, useCallback, useRef } from "react";
import CompanyLogo from "../company-logo";
import { LoadingSpinner } from "../spinner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip";
import { ArrowLeft } from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import { getCompanyDisplayName } from "@/utils/company-display-name";

export interface TrackableCompanyTitleProps {
    company: Company;
    isPurchased: boolean;
    size?: 'xsmall' | 'small' | 'large';
    isLoading?: boolean;
    companyIdHeader?: boolean;
    additionalButtons?: React.ReactNode;
}

export const TrackableCompanyTitle = ({ company, size, isLoading, companyIdHeader = false, isPurchased, additionalButtons }: TrackableCompanyTitleProps) => {

    const displayName = getCompanyDisplayName(company);
    const [loading, setLoading] = useState(isLoading);
    const router = useRouter();
    const pathname = usePathname();
    const [showBackButton, setShowBackButton] = useState(false);
    const lastPathRef = useRef<string | null>(null);

    useEffect(() => {
        setLoading(isLoading);
        setShowBackButton(window.history.length > 1);
    }, [isLoading]);

    useEffect(() => {
        lastPathRef.current = pathname;
    }, [pathname]);

    const handleBack = useCallback(() => {
        router.back();

        const handlePathChange = () => {
            const newPath = window.location.pathname;
            if (newPath.startsWith('/company/')) {
                router.replace('/company', { scroll: false });
            }
        };

        window.addEventListener('popstate', handlePathChange, { once: true });
    }, [router]);

    return (
        <div className={`gap-2 ${companyIdHeader ? 'flex items-center justify-between w-full' : 'flex items-center w-full'}`}>
            <div className={`text-black ${size && size === 'small' ? 'text-lg font-medium' : size && size === 'xsmall' ? 'text-base font-normal' : 'text-3xl font-bold'} flex gap-3 items-center`}>
                <div className="flex items-center gap-3">
                    {companyIdHeader && showBackButton && (
                        <button
                            onClick={handleBack}
                            className="h-8 p-1.5 bg-white rounded border border-[#d7d5d2] justify-center items-center inline-flex"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                    )}
                    <CompanyLogo logo={company.logo || displayName} alt={displayName} id={company.id} size={size === 'xsmall' ? 'md' : 'lg'} />
                </div>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className={`text-gray-900 truncate ${size === 'large' ? 'w-full' : 'max-w-[160px] md:max-w-[120px]'} overflow-hidden text-ellipsis`}
                                style={(size === 'xsmall' ? {
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    WebkitLineClamp: 2,
                                    wordBreak: 'break-word',
                                } : {})}>
                                <div className="flex md:block items-center gap-2">
                                    <div className={`overflow-hidden text-ellipsis whitespace-nowrap ${companyIdHeader ? 'max-w-[200px] md:max-w-[calc(100vw_-_700px)] md:w-full' : ''}`}>{displayName}</div>
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            {displayName}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            {companyIdHeader && (
                <div className="hidden md:flex items-center gap-4 z-10">
                    {additionalButtons}
                </div>
            )}
        </div>
    );
}
